import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, formatMoney } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { fbTrack } from "@/lib/pixel";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const location = useLocation();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    const ppOrder = location.state?.order;
    if (ppOrder) {
      setOrder(ppOrder);
      setState("paid");
      if (!cleared.current) {
        clearCart();
        cleared.current = true;
        fbTrack("Purchase", {
          value: ppOrder.total,
          currency: (ppOrder.currency || "nzd").toUpperCase(),
          content_ids: (ppOrder.items || []).map((i) => i.product_id),
          content_type: "product",
        });
      }
      return;
    }
    if (!sessionId) {
      setState("failed");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data.order);
          setState("paid");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
            if (data.order) {
              fbTrack("Purchase", {
                value: data.order.total,
                currency: (data.order.currency || "nzd").toUpperCase(),
                content_ids: (data.order.items || []).map((i) => i.product_id),
                content_type: "product",
              });
            }
          }
          return;
        }
        if (data.status === "expired" || attempts.current >= 6) {
          setState("failed");
          return;
        }
        attempts.current += 1;
        timer = setTimeout(poll, 2000);
      } catch {
        setState("failed");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-center">
      {state === "checking" && (
        <div data-testid="order-checking">
          <Loader2 size={48} className="mx-auto text-brand animate-spin mb-6" />
          <h1 className="font-display text-2xl font-bold">Confirming your payment…</h1>
          <p className="text-muted-foreground mt-2">Please don't close this page.</p>
        </div>
      )}

      {state === "paid" && (
        <div data-testid="order-success">
          <CheckCircle2 size={56} className="mx-auto text-green-600 mb-6" />
          <h1 className="font-display text-4xl font-black tracking-tighter mb-3">Order confirmed!</h1>
          <p className="text-muted-foreground mb-8">
            Thank you for shopping with Go Big Forever. A confirmation has been recorded for{" "}
            <span className="font-bold text-ink">{order?.customer?.email}</span>.
          </p>
          {order && (
            <div className="border border-ink p-6 text-left mb-8">
              <p className="text-sm text-muted-foreground mb-1">Order ID</p>
              <p className="font-mono font-bold mb-4" data-testid="order-id">{order.id}</p>
              <div className="space-y-2 text-sm">
                {order.items.map((it) => (
                  <div key={`${it.product_id}-${it.size || ""}`} className="flex justify-between">
                    <span>{it.quantity} × {it.name}{it.size ? ` · ${it.size}` : ""}</span>
                    <span>{formatMoney(it.line_total, order.currency)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-ink/10 pt-2 mt-2"><span>{order.gst_label}</span><span>{formatMoney(order.gst_amount, order.currency)}</span></div>
                <div className="flex justify-between font-bold font-display text-lg"><span>Total</span><span>{formatMoney(order.total, order.currency)}</span></div>
              </div>
            </div>
          )}
          <div className="flex gap-4 justify-center">
            <Link to="/track" className="btn-secondary" data-testid="track-from-success">Track order</Link>
            <Link to="/shop" className="btn-primary" data-testid="continue-shopping">Continue shopping</Link>
          </div>
        </div>
      )}

      {state === "failed" && (
        <div data-testid="order-failed">
          <XCircle size={56} className="mx-auto text-brand mb-6" />
          <h1 className="font-display text-3xl font-bold mb-3">Payment not completed</h1>
          <p className="text-muted-foreground mb-8">Your payment was cancelled or could not be verified. Your cart is still saved.</p>
          <Link to="/cart" className="btn-primary">Back to cart</Link>
        </div>
      )}
    </div>
  );
}
